import { debounce, isEmpty, isObject } from "lodash";
import { isLocal, isDev, isInternal } from "./env";

interface GroupSettings {
  defaultType?: string;
  collapsed?: boolean;
  disable?: boolean;
}

type consoleOptions = keyof typeof console;

// =================================
// Basic Logging Middleware ========
// =================================

let allowSend = false;

export function togglePermission(allow: boolean = !allowSend) {
  allowSend = allow;
}

// Won't display in console by default
export const debug = (...args: any) => allowSend && console.debug.apply(null, args);
export const log = (...args: any) => allowSend && console.log.apply(null, args);
export const info = (...args: any) => allowSend && console.info.apply(null, args);
export const trace = (...args: any) => allowSend && console.trace.apply(null, args);
export const dir = (...args: any) => allowSend && console.dir.apply(null, args);
export const table = (...args: any) => allowSend && console.table.apply(null, args);
export const warn = (...args: any) => allowSend && console.warn.apply(null, args);
export const error = (...args: any) => allowSend && console.error.apply(null, args);
export const assert = (...args: any) => allowSend && console.assert.apply(null, args);

/**
 * Wrapper for emitting a console group
 * const Landing = new Group("Test", "info", true);
 * Landing.emit("First Message", {error: "Error Message"}).emit("Another message")
 * Landing.close() = Stop adding messages and execute the logs (debounced)
 * Landing.end()   = Immediately execute the messages and close the group;
 * @type  {Group}
 * @param {string}  groupName             "Person"
 * @param {object}  [options]             Object of options if not using default
 * @param {string}  [options.defaultType] "log"
 * @param {boolean} [options.collapsed]   Whether to default the group as collapsed in the window
 * @param {boolean} [options.disable]     Prevent writing to the console
 */
export class Group {
  type: consoleOptions;
  private readonly collapsed: boolean;
  private readonly disabled: boolean;
  private messages: any[];

  constructor(
    private readonly name: string,
    { defaultType = "log", collapsed = false, disable = false }: GroupSettings = {}
  ) {
    this.type = (defaultType || "log") as consoleOptions;
    this.collapsed = collapsed || false;
    this.disabled = disable;
    this.messages = [];

    if (!console || !console.group) {
      console.error("Browser does not yet support console.group");
      return;
    }

    if (disable) {
      allowSend && console.info(`${this.name} log disabled`);
      return;
    }

    if (defaultType && !new RegExp(Object.keys(console).join("|")).test(defaultType)) {
      throw new Error(`Invalid console [defaultType] option selected: [${defaultType}]`);
    }

    this.emit(`Created new console group: [${this.name}]`);
  }

  emit(...options: any) {
    if (this.disabled) {
      return this;
    }

    this.messages.push(...options);
    this.close();
    return this;
  }

  /**
   * Close the chain
   * @param delay
   */
  close = (() => debounce(this.end, 2000))();

  end() {
    if (this.disabled || !allowSend || isEmpty(this.messages)) return;

    const type = this.type;
    console[this.collapsed ? "groupCollapsed" : "group"](this.name);

    // Iterate through messages
    this.messages.forEach((msg) => {
      if (isEmpty(msg)) return;

      if (typeof msg === "boolean") {
        msg = String(msg);
      }

      // @ts-ignore
      isObject(msg)
        ? Object.entries(msg).forEach(([type, msg]) => console[type as consoleOptions](msg))
        : console[type](msg);
    });

    // Clear messages
    this.messages = [];
    console.groupEnd();

    return this;
  }
}

if (isLocal) {
  allowSend = true;
  log("logger.js:", "Local env detected - allowing logging");
} else if (isDev) {
  allowSend = true;
  log("logger.js:", "Dev env detected - allowing logging");
} else if (isInternal) {
  allowSend = true;
  log("logger.js:", "Internal user detected - allowing logging");
}
